import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Calendar, ArrowRight } from 'lucide-react';
import { fetchCollection } from '../../lib/cms';
import SEO from '../../components/SEO';

export default function KundeudtalelserEn() {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    async function loadData() {
      const data = await fetchCollection('testimonials', 'en');
      setTestimonials(data);
    }
    loadData();
  }, []);

  return (
    <div className="relative min-h-screen bg-slate-50 text-slate-900 antialiased">
      <SEO
        title="Customer Reviews & Testimonials"
        description="Read what professional equestrian stables and horse owners say about the Danshaker sorting machine. Save up to 50% time on daily stable chores."
        path="/en/reviews"
        lang="en"
      />

      {/* Background gradients */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(14,103,162,0.05),transparent_60%)]" />

      {/* Header Section */}
      <section className="mx-auto max-w-7xl px-4 pt-16 pb-12 md:px-8 md:pt-24">
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#0E67A2]/10 px-4 py-1.5 text-xs font-bold text-[#0E67A2]">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            <span>Customer Experiences</span>
          </div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            Customer Testimonials
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            Read first-hand accounts from horse owners, riding schools, and training stables that have made their daily stable chores lighter with a Danshaker.
          </p>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-8">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {testimonials.map((item) => (
            <div
              key={item.id}
              className="flex flex-col justify-between rounded-3xl border border-slate-200 bg-white p-8 shadow-xl transition hover:shadow-2xl hover:border-[#0E67A2]/20"
            >
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  {item.image && item.image[0] ? (
                    <img
                      src={item.image[0]}
                      alt={item.name}
                      width="64"
                      height="64"
                      loading="lazy"
                      decoding="async"
                      className="h-16 w-16 rounded-full object-cover border border-slate-200"
                    />
                  ) : (
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#0E67A2]/10 text-[#0E67A2] font-black text-2xl">
                      {item.name ? item.name.charAt(0) : 'K'}
                    </div>
                  )}
                  <div>
                    <h3 className="font-bold text-slate-900 text-lg">{item.name}</h3>
                    <p className="text-xs text-slate-500 font-medium">{item.company}</p>
                    <div className="flex items-center gap-1 text-amber-400 text-xs mt-1" aria-label="5 out of 5 stars">
                      <i className="fa-solid fa-star" />
                      <i className="fa-solid fa-star" />
                      <i className="fa-solid fa-star" />
                      <i className="fa-solid fa-star" />
                      <i className="fa-solid fa-star" />
                    </div>
                  </div>
                </div>

                <div
                  className="text-slate-700 leading-relaxed text-sm italic font-medium space-y-2"
                  dangerouslySetInnerHTML={{ __html: item.text }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* CTA Bottom Banner */}
        <div className="mt-16 rounded-3xl bg-[#0E67A2] p-8 md:p-12 text-center text-white space-y-6 shadow-2xl">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Want to test Danshaker in your stable?</h2>
          <p className="mx-auto max-w-2xl text-slate-100 text-base md:text-lg">
            We offer a completely free and non-binding trial demonstration. You test the machine directly on your own horse boxes.
          </p>
          <div className="pt-2">
            <Link
              to="/en/free-demo"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-8 py-4 font-bold text-[#0E67A2] shadow-lg transition hover:bg-slate-100"
            >
              <Calendar className="h-5 w-5" />
              Book free demo today
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
